import React, { useState, useEffect, useMemo } from 'react';
import { CategorySubcategorySelect } from './CategorySubcategorySelect';

interface Transaction {
  id: number;
  date: string;
  amount: number;
  merchant_name: string | null;
  description: string;
  category_id: number | null;
  subcategory_id: number | null;
  category_name?: string;
  subcategory_name?: string;
  account_name?: string;
  institution_name?: string;
}

interface Category {
  id: number;
  name: string;
  subcategories: Subcategory[];
}

interface Subcategory {
  id: number;
  name: string;
}

interface TransactionReassignment {
  id: number;
  newCategoryId: number | null;
  newSubcategoryId: number | null;
  status: 'unchanged' | 'reassigned' | 'will-uncategorize';
}

interface SubcategoryReassignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  subcategory: { id: number; name: string; categoryName: string } | null;
  transactions: Transaction[];
  categories: Category[];
  onSave: (reassignments: TransactionReassignment[], subcategoryId: number) => Promise<void>;
}

export const SubcategoryReassignmentModal: React.FC<SubcategoryReassignmentModalProps> = ({
  isOpen,
  onClose,
  subcategory,
  transactions,
  categories,
  onSave,
}) => {
  const [searchText, setSearchText] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [hideReassigned, setHideReassigned] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState<Set<number>>(new Set());
  const [reassignments, setReassignments] = useState<Map<number, TransactionReassignment>>(new Map());
  const [bulkCategoryId, setBulkCategoryId] = useState<number | null>(null);
  const [bulkSubcategoryId, setBulkSubcategoryId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSearchText('');
      setMinAmount('');
      setMaxAmount('');
      setHideReassigned(false);
      setSelectedTransactions(new Set());
      setReassignments(new Map());
      setBulkCategoryId(null);
      setBulkSubcategoryId(null);
    }
  }, [isOpen, subcategory]);

  // Calculate totals
  const totalAmount = transactions.reduce((sum, t) => sum + Math.abs(t.amount), 0);
  const totalCount = transactions.length;

  // Filter transactions
  const filteredTransactions = useMemo(() => {
    return transactions.filter(transaction => {
      // Text search
      if (searchText) {
        const searchLower = searchText.toLowerCase();
        const merchantMatch = transaction.merchant_name?.toLowerCase().includes(searchLower);
        const descriptionMatch = transaction.description.toLowerCase().includes(searchLower);
        if (!merchantMatch && !descriptionMatch) return false;
      }

      // Amount filtering
      const amount = Math.abs(transaction.amount);
      if (minAmount && amount < parseFloat(minAmount)) return false;
      if (maxAmount && amount > parseFloat(maxAmount)) return false;

      // Hide reassigned filter
      if (hideReassigned) {
        const reassignment = reassignments.get(transaction.id);
        if (reassignment && reassignment.status === 'reassigned') return false;
      }

      return true;
    });
  }, [transactions, searchText, minAmount, maxAmount, hideReassigned, reassignments]);

  // Get reassignment status for a transaction
  const getTransactionStatus = (transactionId: number) => {
    const reassignment = reassignments.get(transactionId);
    if (!reassignment) return { status: 'unchanged', displayText: 'Uncategorized' };
    
    if (reassignment.status === 'reassigned') {
      const category = categories.find(c => c.id === reassignment.newCategoryId);
      const subcategory = category?.subcategories.find(s => s.id === reassignment.newSubcategoryId);
      
      if (subcategory) {
        return { status: 'reassigned', displayText: `NEW: ${category!.name} - ${subcategory.name}` };
      } else if (category) {
        return { status: 'reassigned', displayText: `NEW: ${category.name}` };
      }
    }
    
    return { status: 'will-uncategorize', displayText: 'Uncategorized' };
  };

  // Handle bulk apply
  const handleBulkApply = () => {
    if (!bulkCategoryId || selectedTransactions.size === 0) return;

    const newReassignments = new Map(reassignments);
    
    selectedTransactions.forEach(transactionId => {
      newReassignments.set(transactionId, {
        id: transactionId,
        newCategoryId: bulkCategoryId,
        newSubcategoryId: bulkSubcategoryId,
        status: 'reassigned'
      });
    });

    setReassignments(newReassignments);
    setSelectedTransactions(new Set());
    setBulkCategoryId(null);
    setBulkSubcategoryId(null);
  };

  // Handle select all
  const handleSelectAll = () => {
    setSelectedTransactions(new Set(filteredTransactions.map(t => t.id)));
  };

  // Handle clear selection
  const handleClearSelection = () => {
    setSelectedTransactions(new Set());
  };

  // Handle individual transaction selection
  const handleTransactionSelection = (transactionId: number) => {
    const newSelected = new Set(selectedTransactions);
    if (newSelected.has(transactionId)) {
      newSelected.delete(transactionId);
    } else {
      newSelected.add(transactionId);
    }
    setSelectedTransactions(newSelected);
  };

  // Calculate status counts
  const statusCounts = useMemo(() => {
    let reassigned = 0;
    let willUncategorize = 0;
    
    transactions.forEach(transaction => {
      const reassignment = reassignments.get(transaction.id);
      if (reassignment && reassignment.status === 'reassigned') {
        reassigned++;
      } else {
        willUncategorize++;
      }
    });

    return { reassigned, willUncategorize };
  }, [transactions, reassignments]);

  // Handle save
  const handleSave = async () => {
    if (!subcategory) return;

    setSaving(true);
    try {
      // Include ALL transactions from the subcategory, not just reassigned ones
      const reassignmentList = transactions.map(transaction => {
        const reassignment = reassignments.get(transaction.id);
        if (reassignment && reassignment.status === 'reassigned') {
          // Explicitly reassigned transaction
          return reassignment;
        } else {
          // Transaction should be uncategorized
          return {
            id: transaction.id,
            newCategoryId: null,
            newSubcategoryId: null,
            status: 'will-uncategorize' as const
          };
        }
      });
      
      await onSave(reassignmentList, subcategory.id);
      onClose();
    } catch (error) {
      console.error('Error saving reassignments:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !subcategory) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-900 text-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-white">⚠️ Delete Subcategory: "{subcategory.categoryName} - {subcategory.name}"</h2>
            <div className="text-sm text-gray-400">
              📊 {totalCount} transactions • ${totalAmount.toFixed(2)} total
            </div>
          </div>
          <button 
            className="text-gray-400 hover:text-white transition-colors"
            onClick={onClose}
          >
            <span className="text-2xl">×</span>
          </button>
        </div>
        
        <div className="overflow-y-auto flex-1 p-4">
          {/* Compact Filters */}
          <div className="mb-4 flex items-center space-x-4 text-sm">
            <input
              type="text"
              placeholder="Search transactions..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="px-3 py-1 bg-gray-800 border border-gray-600 rounded text-white placeholder-gray-400"
            />
            <input
              type="number"
              placeholder="Min $"
              value={minAmount}
              onChange={(e) => setMinAmount(e.target.value)}
              className="w-20 px-2 py-1 bg-gray-800 border border-gray-600 rounded text-white placeholder-gray-400"
              step="0.01"
            />
            <input
              type="number"
              placeholder="Max $"
              value={maxAmount}
              onChange={(e) => setMaxAmount(e.target.value)}
              className="w-20 px-2 py-1 bg-gray-800 border border-gray-600 rounded text-white placeholder-gray-400"
              step="0.01"
            />
            <label className="flex items-center text-gray-300">
              <input
                type="checkbox"
                checked={hideReassigned}
                onChange={(e) => setHideReassigned(e.target.checked)}
                className="mr-2"
              />
              Hide reassigned
            </label>
          </div>

          {/* Bulk Actions - Only show when transactions are selected */}
          {selectedTransactions.size > 0 && (
            <div className="mb-4 p-3 bg-gray-800 rounded border border-gray-700">
              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-300">{selectedTransactions.size} selected</span>
                <div className="flex-1 max-w-xs">
                  <CategorySubcategorySelect
                    categories={categories}
                    currentCategoryId={bulkCategoryId}
                    currentSubcategoryId={bulkSubcategoryId}
                    isIncome={false}
                    placeholder="Select new category..."
                    onChange={(categoryId, subcategoryId) => {
                      setBulkCategoryId(categoryId);
                      setBulkSubcategoryId(subcategoryId);
                    }}
                  />
                </div>
                <button 
                  className="px-3 py-1 bg-blue-600 text-white hover:bg-blue-700 rounded text-sm disabled:opacity-50"
                  onClick={handleBulkApply}
                  disabled={!bulkCategoryId}
                >
                  Apply to Selected
                </button>
                <button 
                  className="px-3 py-1 bg-gray-600 text-white hover:bg-gray-700 rounded text-sm"
                  onClick={handleClearSelection}
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          {/* Transaction List */}
          <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
            <div className="max-h-96 overflow-y-auto">
              {filteredTransactions.map((transaction) => {
                const isSelected = selectedTransactions.has(transaction.id);
                const status = getTransactionStatus(transaction.id);
                
                return (
                  <div 
                    key={transaction.id} 
                    className={`flex items-center p-3 border-b border-gray-700 last:border-b-0 hover:bg-gray-750 transition-colors ${
                      isSelected ? 'bg-blue-900/30 border-l-4 border-l-blue-500' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleTransactionSelection(transaction.id)}
                      className="mr-3 h-4 w-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <div className="flex-1 min-w-0 grid grid-cols-12 gap-4 items-center">
                      {/* Description & Account */}
                      <div className="col-span-4 min-w-0">
                        <p className="text-sm font-medium text-white truncate">
                          {transaction.merchant_name || transaction.description}
                        </p>
                        <p className="text-xs text-gray-400 truncate">
                          {new Date(transaction.date).toLocaleDateString()} • {transaction.institution_name} - {transaction.account_name}
                        </p>
                      </div>
                      
                      {/* Amount */}
                      <div className="col-span-1 text-right">
                        <p className={`text-sm font-semibold ${
                          transaction.amount < 0 ? 'text-red-400' : 'text-green-400'
                        }`}>
                          ${Math.abs(transaction.amount).toFixed(2)}
                        </p>
                      </div>
                      
                      {/* Category Dropdown */}
                      <div className="col-span-5">
                        <CategorySubcategorySelect
                          categories={categories}
                          currentCategoryId={reassignments.get(transaction.id)?.newCategoryId || null}
                          currentSubcategoryId={reassignments.get(transaction.id)?.newSubcategoryId || null}
                          isIncome={false}
                          onChange={(categoryId, subcategoryId) => {
                            const newReassignments = new Map(reassignments);
                            newReassignments.set(transaction.id, {
                              id: transaction.id,
                              newCategoryId: categoryId,
                              newSubcategoryId: subcategoryId,
                              status: 'reassigned'
                            });
                            setReassignments(newReassignments);
                          }}
                        />
                      </div>
                      
                      {/* Status */}
                      <div className="col-span-2 text-right">
                        <span className={`text-xs font-medium ${
                          status.status === 'reassigned' ? 'text-green-400' : 
                          status.status === 'will-uncategorize' ? 'text-yellow-400' : 
                          'text-gray-400'
                        }`}>
                          {status.displayText}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>

        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-700 bg-gray-800">
          <button 
            className="px-4 py-2 bg-gray-600 text-white hover:bg-gray-700 rounded transition-colors"
            onClick={onClose} 
            disabled={saving}
          >
            Cancel
          </button>
          <button 
            className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded transition-colors disabled:opacity-50"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save & Delete Subcategory'}
          </button>
        </div>
      </div>
    </div>
  );
};
